import React from 'react';
import { UserPresence } from '../types';
import { collaboratorTone } from '../hooks/usePresence';

interface PresenceAvatarsProps {
  users: UserPresence[];
  currentUserId?: string;
  maxVisible?: number;
}

export const PresenceAvatars: React.FC<PresenceAvatarsProps> = ({
  users,
  currentUserId,
  maxVisible = 5,
}) => {
  // Filter out current user and show others
  const otherUsers = users.filter(u => u.userId !== currentUserId);

  if (otherUsers.length === 0) return null;

  const visibleUsers = otherUsers.slice(0, maxVisible);
  const overflowCount = otherUsers.length - maxVisible;

  return (
    <div className="flex items-center gap-1">
      <div className="flex -space-x-2">
        {visibleUsers.map(user => (
          <div
            key={user.userId}
            data-tone={collaboratorTone(user.userId)}
            className="relative flex group"
          >
            {/* A photo is ringed in the user's tone; without one the tone is the avatar itself */}
            {user.photoURL ? (
              <span className="ns-avatar [--size:28px] ring-2 ring-[color:var(--tone)]">
                <img src={user.photoURL} alt={user.displayName} />
              </span>
            ) : (
              <div className="ns-avatar [--size:28px] bg-[color:var(--tone)] text-ink ring-2 ring-white">
                {getInitials(user.displayName)}
              </div>
            )}
            {/* Online indicator */}
            <span
              className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-ok"
            />
            {/* Tooltip: under the avatar, which sits in the top bar with no room above it */}
            <div className="ns-tooltip absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
              {user.displayName}
            </div>
          </div>
        ))}
        {overflowCount > 0 && (
          <div className="ns-avatar [--size:28px] bg-cream-200 text-ink-soft ring-2 ring-white">
            +{overflowCount}
          </div>
        )}
      </div>
      <span className="ns-t-meta ml-1.5 whitespace-nowrap">
        {otherUsers.length} online
      </span>
    </div>
  );
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}
